import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import CTA from 'Src/components/buttons/CTA';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';

import { useOnErrorShowToast } from '../toasts/useOnErrorShowToast';

import { useCashBank } from './useCashBank';

const { Form, Field } = getTypedFormComponents<FormModel>();

class FormModel {
  cashBankId: number;
  amount: number;
  notes?: string;

  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

const CashBankEditDeposit: FC = () => {
  const { t } = useTranslation();
  const handleFormSubmitError = useOnErrorShowToast({ title: t('cashBank.errors.deposit') });
  const { data: currentCashBank } = useCashBank.query.current();
  const { mutate } = useCashBank.mutate.deposit(handleFormSubmitError);

  const form = useForm<FormModel>({
    defaultValues: {
      ...new FormModel({ cashBankId: currentCashBank?.id }),
    },
    schema: object().shape({
      amount: number().label(t('cashBank.labels.depositAmount')).required().min(0),
    }),
  });

  const modal = useModalControl({
    onOpen: () => {
      form.action.reset({
        ...new FormModel({
          cashBankId: currentCashBank?.id,
        })
      });
    }
  });

  const onSave = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      modal.close();
      mutate(values);
    });
  };

  return (
    <>
      <CTA onClick={modal.open} label={t('cashBank.labels.deposit')} />
      <Modal
        open={modal.isOpen}
        onBackDropClick={modal.close}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={modal.close} />}>
          <ModalTitle copy={t('cashBank.labels.depositAmount')} />
        </ModalHeader>

        <ModalContent>
          <Form usedForm={form} omitChangeTracking>
            <Field
              type="currency"
              name="amount"
              label={t('cashBank.labels.enterAmountDollars')}
            />
            <Field type="text" name="notes" multiline label={t('cashBank.labels.additionalNotes')} />
          </Form>
        </ModalContent>

        <ModalActions>
          <ModalAction color="primary" onClick={() => void onSave()} label={t('cashBank.labels.depositRecord')} />
        </ModalActions>


      </Modal>
    </>
  );
};

export default CashBankEditDeposit;
